/**
 * A refusal in the reader's language (docs/LOG.md 2026-10-04, expert review batch O-D, OLV-07). The server sends a
 * code and parameters, never a sentence for the screen; the screen prints errors.<code> with the parameters, and the
 * parameters that are themselves stored codes (a role, a state, an anchor, a category) in words too. The server's
 * English sentence and any detail stay out of the page: the server logs them.
 *
 * PROVENANCE: the pattern of C:\Valentin\src\components\ErrorNote.tsx (useErrorText) @ 2410877, read-only: the code's
 * key with its parameters, or a generic sentence.
 */

import type { Language, Translate } from '../i18n/core'
import { isMessageKey, localeOf } from '../i18n/core'
import type { MessageKey } from '../i18n/en'
import { ApiError } from './api'
import { formatDay, formatMonthDay } from './dates'

type Params = Readonly<Record<string, string | number>>

/** Parameters that are stored codes, by refusal: the label prefix each is shown through. */
const CODED: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  FIRM_ROLE_NEEDED: { roles: 'firmRole' },
  CLIENT_ROLE_NEEDED: { roles: 'role' },
  ASSIGNEE_NEEDS_ROLE: { role: 'role' },
  CYCLE_ITEM_STATE: { from: 'occurrenceStatus', to: 'occurrenceStatus' },
  ACTION_STATE: { from: 'actionStatus', to: 'actionStatus' },
  CLIENT_STATE: { from: 'status', to: 'status' },
  WHEEL_ANCHOR_MISSING: { anchor: 'anchor' },
  RULE_DROPS_ANCHOR: { anchor: 'anchor' },
  BASIS_FIELDS_NEEDED: { category: 'category' },
  BASIS_PURPOSE_FIELDS_NEEDED: { category: 'category', purpose: 'purpose' },
  BASIS_RETENTION_NEEDED: { category: 'category' },
  ROLE_NOT_HOLDABLE_HERE: { mode: 'roleMode' },
  ROLE_BASIS_NEEDED: { mode: 'roleMode' },
  // Ask Olivia (H3b-6): why the guide or its diagrams are not used, in words.
  HELP_GUIDE_UNAVAILABLE: { reason: 'help.fault' },
  HELP_DIAGRAMS_UNAVAILABLE: { reason: 'help.fault' },
}

/** "lead or reviewer", "klientansvarig eller granskare". */
function orList(items: string[], language: Language): string {
  try {
    return new Intl.ListFormat(localeOf(language), { style: 'long', type: 'disjunction' }).format(items)
  } catch {
    return items.join(', ')
  }
}

/** A refusal's parameters in the reader's language: codes as labels, titles in the reader's language. */
export function errorParams(code: string, params: Params | undefined, t: Translate, language: Language): Record<string, string | number> {
  const out: Record<string, string | number> = { ...(params ?? {}) }
  const coded = CODED[code] ?? {}
  for (const [name, prefix] of Object.entries(coded)) {
    const v = out[name]
    if (typeof v !== 'string') continue
    out[name] = orList(
      v.split(',').filter(Boolean).map((x) => {
        const key = `${prefix}.${x}`
        return isMessageKey(key) ? t(key) : x
      }),
      language,
    )
  }
  // A financial year end, 'MM-DD', as the reader's language writes a day of the year (OLV-25).
  if (typeof out.yearEnd === 'string') out.yearEnd = formatMonthDay(out.yearEnd, language)
  // A day, 'YYYY-MM-DD', as the reader's language writes it (a move's latest date: the review follow-up).
  if (typeof out.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(out.date)) out.date = formatDay(out.date, language)
  // A library title comes in both languages: the reader's one.
  if (typeof out.title_en === 'string' || typeof out.title_sv === 'string') {
    out.title = (language === 'sv' ? out.title_sv || out.title_en : out.title_en || out.title_sv) ?? ''
  }
  // A jurisdiction the server names in both languages (IP0): its name in the reader's language, else its code.
  if (code === 'MAP_NOT_FOR_JURISDICTION' && typeof out.title === 'string' && out.title) out.jurisdiction = out.title
  // The tools the participants said yes to, when a transcript names another (the review round of wave 2, finding 18).
  if (code === 'CONSENT_MISSING') out.tools = typeof out.tools === 'string' && out.tools ? t('errors.consentTools', { tools: out.tools }) : ''
  // What a refused rule belongs to: an item by its title, a series by its code, else the rule being written.
  if (code.startsWith('WHEEL_')) {
    out.what = out.title ? t('errors.subject.item', { title: out.title }) : typeof out.series === 'string' ? t('errors.subject.series', { code: out.series }) : t('errors.subject.rule')
  }
  return out
}

/** The sentence for a refusal by its code; a code this app does not know answers with the generic one of its status. */
export function refusalText(code: string, status: number, params: Params | undefined, t: Translate, language: Language): string {
  const key = `errors.${code}`
  if (isMessageKey(key)) return t(key, errorParams(code, params, t, language))
  const generic: MessageKey = status === 403 ? 'errors.FORBIDDEN' : status === 404 ? 'errors.NOT_FOUND' : status === 409 ? 'errors.CONFLICT' : status >= 400 && status < 500 ? 'errors.INVALID' : 'errors.INTERNAL'
  return t(generic)
}

/** Any error a screen shows, in words: a refusal by its code, a failure to reach the server by a fixed sentence. */
export function errorText(error: unknown, t: Translate, language: Language): string {
  if (error instanceof ApiError) return refusalText(error.code, error.status, error.params, t, language)
  return t('errors.UNREACHABLE')
}
